import React, { useEffect, useMemo, useState } from "react";
import { notifyError } from "../../utils/tostfy";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { handleApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";
import EyeIcon from "../../assets/image/eye-icon.svg";
import FilterIcon from "../../assets/image/filter.svg";
import SingleDatePicker from "../../Components/SingleDatePicker/SingleDatePicker";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import { SEARCH_MAX_LENGTH, clampSearchInput } from "../../utils/search";
import { formatInr as formatFuelAmount } from "../../utils/formatAmount";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";
import TripMeterStatusBadge from "../../Components/TripMeterStatusBadge";
import { isTripMeterStopped } from "../../utils/meterSensorIssue";

const emptyStats = { vehicles: "—", fuel: "—", amount: "—" };

const formatHistoryDate = (iso) => {
  if (iso == null || iso === "") return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const mapFuelHistoryRow = (h, index, page, limit) => {
  const pumpRef = h?.fuel_pump_id;
  const pumpName =
    (typeof pumpRef === "object" && pumpRef?.fuel_pump_name) ||
    (typeof pumpRef === "object" && pumpRef?.name) ||
    "—";
  const veh = h?.vehicle_id;
  const reg =
    (typeof veh === "object" && (veh?.registration_number || veh?.name)) || "—";
  const qty = h?.fuel_quantity;
  const qtyDisplay = qty != null && qty !== "" ? `${Number(qty).toLocaleString("en-IN")} L` : "—";

  const effRaw = h?.efficency ?? h?.efficiency;
  const efficiencyDisplay =
    effRaw != null && effRaw !== "" && !Number.isNaN(Number(effRaw))
      ? Number(effRaw).toLocaleString("en-IN", { maximumFractionDigits: 2 })
      : "—";

  const currOdo = h?.odometer_reading;
  const prevOdo =
    typeof veh === "object" && veh != null ? veh?.odometer_reading : null;
  let distanceDisplay = "—";
  if (
    currOdo != null &&
    currOdo !== "" &&
    prevOdo != null &&
    prevOdo !== "" &&
    !Number.isNaN(Number(currOdo)) &&
    !Number.isNaN(Number(prevOdo))
  ) {
    const diff = Number(currOdo) - Number(prevOdo);
    if (Number.isFinite(diff)) {
      distanceDisplay = `${diff.toLocaleString("en-IN", { maximumFractionDigits: 2 })} km`;
    }
  }

  return {
    id: h?._id ?? h?.id ?? `row-${index}`,
    sno: (page - 1) * limit + index + 1,
    date: formatHistoryDate(h?.createdAt),
    pump: pumpName,
    vehicle: reg,
    odometer: h?.odometer_reading != null ? String(h.odometer_reading) : "—",
    qty: qtyDisplay,
    amount: formatFuelAmount(h?.fuel_rate),
    distance: distanceDisplay,
    efficiency: efficiencyDisplay,
    meterStopped: isTripMeterStopped(h),
  };
};

const buildTabsFromDetail = (detail) => {
  const pumps = detail?.fuel_pump_data;
  if (!Array.isArray(pumps) || pumps.length === 0) {
    return [{ id: "no-pump", label: "Fuel pump" }];
  }
  return pumps.map((fp, i) => ({
    id: fp._id || fp?.id || `pump-${i}`,
    label: fp.fuel_pump_name || fp.name || `Fuel pump ${i + 1}`,
  }));
};

const normalizeDetail = (d) => {
  if (!d || typeof d !== "object") return null;
  const code = (d.country_code || "").trim();
  const num = (d.mobile_number ?? "").toString().trim();
  const phone = code && num ? `${code} ${num}` : num || code || "—";
  const loc =
    d.mipl_location_id?.name ||
    d.mipl_location?.name ||
    "—";
  const pumps = Array.isArray(d.fuel_pump_data) ? d.fuel_pump_data : [];
  const pumpNames =
    pumps
      .map((p) => p?.fuel_pump_name || p?.name)
      .filter(Boolean)
      .join(", ") || "—";
  const status =
    d.status === 1 ? "Active" : d.status === 0 ? "Inactive" : "—";

  return {
    ...d,
    displayPhone: phone,
    displayLocation: loc,
    displayPumpNames: pumpNames,
    displayStatus: status,
  };
};

const ViewFuelOperatorDetails = () => {
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const fuelOperatorId = (searchParams.get("fuel_operator_id") || "").trim();

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedDate, setSelectedDate] = useState("");

  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [stats, setStats] = useState(emptyStats);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);

  const tabs = useMemo(() => buildTabsFromDetail(detail), [detail]);

  const [activeTab, setActiveTab] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(clampSearchInput(search).trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!tabs.length || !tabs[0]?.id) return;
    setActiveTab((prev) => (tabs.some((tab) => tab.id === prev) ? prev : tabs[0].id));
  }, [tabs]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, activeTab, selectedDate]);

  useEffect(() => {
    setSubheading("View Fuel Operator Details");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(() => {
    const load = async () => {
      if (!fuelOperatorId) {
        setLoading(false);
        setDetail(null);
        return;
      }
      setLoading(true);
      try {
        const response = await handleApiCall(
          "get",
          `${endpoint.viewFuelOperator}?fuel_operator_id=${encodeURIComponent(fuelOperatorId)}`,
          {}
        );
        if (response?.data?.code === 200) {
          const raw = response?.data?.data;
          setDetail(normalizeDetail(raw));
        } else {
          const errMsg =
            response?.response?.data?.errorMessage ||
            response?.response?.data?.message ||
            response?.data?.message ||
            "Failed to load fuel operator";
          notifyError(errMsg);
          setDetail(null);
        }
      } catch {
        notifyError("Failed to load fuel operator");
        setDetail(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [fuelOperatorId]);

  useEffect(() => {
    const pumpId = (activeTab || "").trim();
    if (!pumpId || pumpId === "no-pump") {
      setHistoryRows([]);
      setStats(emptyStats);
      setHistoryTotal(0);
      setHistoryTotalPages(1);
      setHistoryLoading(false);
      return;
    }

    const loadHistory = async () => {
      setHistoryLoading(true);
      try {
        const q = new URLSearchParams({
          search: clampSearchInput(debouncedSearch).trim(),
          page: String(currentPage),
          limit: String(itemsPerPage),
          fuel_pump_id: pumpId,
        });
        if (selectedDate) q.set("date", selectedDate);
        const response = await handleApiCall(
          "get",
          `${endpoint.listOfFuelHistory}?${q.toString()}`,
          {}
        );
        if (response?.data?.code === 200) {
          const payload = response?.data?.data || {};
          const fd =
            payload.fuel_data &&
            typeof payload.fuel_data === "object" &&
            !Array.isArray(payload.fuel_data)
              ? payload.fuel_data
              : {};
          const raw =
            (Array.isArray(payload.fuel_data) ? payload.fuel_data : null) ||
            payload.fuel_history_data ||
            payload.fuel_history ||
            payload.fuelHistory_data ||
            [];
          const list = Array.isArray(raw) ? raw : [];
          setStats({
            vehicles:
              fd.no_of_vehicles != null && fd.no_of_vehicles !== ""
                ? String(fd.no_of_vehicles)
                : "—",
            fuel:
              fd.total_fuel_liters != null && fd.total_fuel_liters !== ""
                ? `${Number(fd.total_fuel_liters).toLocaleString("en-IN")} L`
                : "—",
            amount:
              fd.fuel_amount != null && fd.fuel_amount !== ""
                ? formatFuelAmount(fd.fuel_amount)
                : "—",
          });
          setHistoryRows(list.map((h, i) => mapFuelHistoryRow(h, i, currentPage, itemsPerPage)));
          setHistoryTotal(typeof payload.total === "number" ? payload.total : list.length);
          setHistoryTotalPages(
            Math.max(1, typeof payload.total_pages === "number" ? payload.total_pages : 1)
          );
        } else {
          setHistoryRows([]);
          setStats(emptyStats);
          setHistoryTotal(0);
          setHistoryTotalPages(1);
          const errMsg =
            response?.response?.data?.errorMessage ||
            response?.response?.data?.message ||
            response?.data?.message ||
            "Failed to load fuel history";
          notifyError(errMsg);
        }
      } catch {
        setHistoryRows([]);
        setStats(emptyStats);
        setHistoryTotal(0);
        setHistoryTotalPages(1);
        notifyError("Failed to load fuel history");
      } finally {
        setHistoryLoading(false);
      }
    };
    loadHistory();
  }, [activeTab, currentPage, itemsPerPage, debouncedSearch, selectedDate]);

  const totalPages = historyTotalPages;

  const TabButton = ({ id, children }) => (
    <button
      type="button"
      onClick={() => {
        setActiveTab(id);
        setCurrentPage(1);
      }}
      className={`btn border-black !rounded-full md:!px-8 md:!py-3 !text-sm mr-2 ${
        activeTab === id
          ? "active !bg-danger text-white !border-danger"
          : "!text-black hover:bg-danger hover:!border-danger"
      }`}
    >
      {children}
    </button>
  );

  if (!fuelOperatorId && !loading) {
    return (
      <>
        <div className="mt-7 px-4">
          <p className="text-gray-600 mb-3">
            Missing fuel operator id. Open view from the list.
          </p>
          <Link to="/fuel-operator-management" className="btn btn-primary">
            Back
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="md:mt-7 mt-3">
        <div className="flex items-center mb-3 md:mb-0">
          <h3 className="md:text-xl font-UbuntuMedium !my-0">Fuel Operator Details</h3>
          <Link to="/fuel-operator-management" className="btn btn-primary ml-auto">
            Back
          </Link>
        </div>

        {loading ? (
          <div className="rounded-lg px-4 py-8 bg-white mb-4 shadow-sm !mt-5 text-center text-gray-500">
            Loading...
          </div>
        ) : detail ? (
          <>
            <div className="mb-4 max-w-screen-lg rounded-lg bg-white px-3 py-2 lg:px-4 lg:py-2 shadow-sm">
              <ul className="grid md:grid-cols-2 [&_li]:flex [&_li]:py-1.5 [&_li]:justify-between md:[&_li:nth-child(odd)]:pr-12 [&_li:nth-child(odd)]:border-r md:[&_li:nth-child(even)]:pl-12 font-UbuntuMedium [&_.label]:text-gray-400 max-[767px]:text-sm">
                <li>
                  <div className="label">Fuel Operator Employee ID</div>
                  <div className="font-UbuntuMedium">{detail.employee_id ?? "—"}</div>
                </li>
                <li>
                  <div className="label">MIPL Location</div>
                  <div className="font-UbuntuMedium">{detail.displayLocation}</div>
                </li>
                <li>
                  <div className="label">Fuel Operator Name</div>
                  <div className="font-UbuntuMedium">{detail.full_name ?? "—"}</div>
                </li>
                <li>
                  <div className="label">Status</div>
                  <div className="font-UbuntuMedium">{detail.displayStatus}</div>
                </li>
                <li>
                  <div className="label">Email</div>
                  <div className="font-UbuntuMedium">{detail.email ?? "—"}</div>
                </li>
                <li>
                  <div className="label">Phone Number</div>
                  <div className="font-UbuntuMedium">{detail.displayPhone}</div>
                </li>
                {/* <li className="col-span-2 flex w-full items-center justify-between gap-4 border-t border-gray-100 !border-r-0 py-2 !pl-0 !pr-0"> */}
                <li>
                  <div className="label shrink-0 text-base leading-normal font-UbuntuMedium text-gray-400">
                    Fuel pumps
                  </div>
                  <div className="min-w-0 flex-1 text-right text-base leading-normal font-UbuntuMedium">
                    {detail.displayPumpNames}
                  </div>
                </li>
                
              </ul>
            </div>

            <div className="mb-2 flex items-start justify-between gap-3 items-center">
              <div className="flex flex-wrap gap-2">
                {tabs.map((tab) => (
                  <TabButton key={tab.id} id={tab.id}>
                    {tab.label}
                  </TabButton>
                ))}
              </div>
              <SingleDatePicker
                variant="popover"
                value={selectedDate}
                onChange={setSelectedDate}
                label="Select date"
                align="right"
                buttonClassName="h-[42px] w-[42px] rounded-lg border shadow-sm inline-flex items-center justify-center bg-white"
                filterIcon={FilterIcon}
                onClear={() => setSelectedDate("")}
              />
            </div>

            <div className="grid gap-4 grid-cols-2 md:grid-cols-3 md:my-6 my-3 max-w-screen-md font-UbuntuRegular">
              <div className="bg-white shadow-sm md:p-4 p-3 rounded-md">
                <h2 className="text-xl font-bold">{stats.vehicles}</h2>
                <p className="text-sm text-gray-500 font-UbuntuMedium">No. Of Vehicle</p>
              </div>

              <div className="bg-white shadow-sm md:p-4 p-3 rounded-md">
                <h2 className="text-xl font-bold">{stats.fuel}</h2>
                <p className="text-sm text-gray-500 font-UbuntuMedium">Diesel</p>
              </div>

              <div className="bg-white shadow-sm md:p-4 p-3 rounded-md">
                <h2 className="text-xl font-bold">{stats.amount}</h2>
                <p className="text-sm text-gray-500 font-UbuntuMedium">Fuel Amount</p>
              </div>
            </div>

            <div className="!mb-2.5 flex items-center justify-between">
              <h3 className="md:text-xl font-UbuntuMedium my-0">Fuel History</h3>
              <input
                type="text"
                placeholder="Search by vehicle no."
                className="border px-3 py-2 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm"
                value={search}
                maxLength={SEARCH_MAX_LENGTH}

                onChange={(e) => {
                  setSearch(clampSearchInput(e.target.value));
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="common-table-wrapper m-0 shadow-sm">
              <div className="table-responsive">
                <table className="w-full text-sm">
                  <thead className="bg-gray-200">
                    <tr>
                      <th>S.No.</th>
                      <th>Date</th>
                      <th>Fuel Pump Name</th>
                      <th>Vehicle Number</th>
                      <th>Odometer Reading</th>
                      <th>Fuel Quantity</th>
                      <th>Fuel Amount</th>
                      <th>Distance Travel (KM)</th>
                      <th>Efficiency</th>
                      <th>Meter</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyLoading && (
                      <tr>
                        <td colSpan={11} className="text-center p-4 text-gray-500">
                          Loading...
                        </td>
                      </tr>
                    )}
                    {!historyLoading && historyRows.length > 0 &&
                      historyRows.map((row) => (
                        <tr key={row.id} className="text-center border-t">
                          <td>{row.sno}</td>
                          <td>{row.date}</td>
                          <td>{row.pump}</td>
                          <td>{row.vehicle}</td>
                          <td>{row.odometer}</td>
                          <td>{row.qty}</td>
                          <td>{row.amount}</td>
                          <td>{row.distance}</td>
                          <td>{row.efficiency + " km/l"}</td>
                          <td className="whitespace-nowrap">
                            <TripMeterStatusBadge stopped={row.meterStopped} />
                          </td>
                          <td>
                            <Link
                              to={`/fuel-operator-management/view-fuel-details?fuel_operator_id=${encodeURIComponent(fuelOperatorId)}&fuel_form_id=${encodeURIComponent(row.id)}`}
                            >
                              <img
                                src={EyeIcon}
                                alt="View"
                                className="inline-block w-5 h-5 mr-2 cursor-pointer"
                              />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    {!historyLoading && historyRows.length === 0 && (
                      <tr>
                        <td colSpan={11} className="text-center p-4">
                          No fuel history available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="flex gap-x-4 items-center mt-4">
              <select
                className="p-2 shadow-sm outline-none max-[767px]:text-xs"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
              </select>
              <div className="text-xs md:text-sm text-gray-500 ml-auto">
                {historyTotal} records found
              </div>
              <div className="flex items-center gap-2 md:pr-8">
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1 || historyTotal === 0 || historyLoading}
                  className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                    currentPage === 1 ? "" : "cursor-pointer"
                  }`}
                >
                  <img src={LeftAngle} className="invert grayscale brightness-100" alt="" />
                </button>
                <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-10 md:w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
                <span className="text-sm text-gray-600 text-nowrap">of {totalPages}</span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || historyTotal === 0 || historyLoading}
                  className={`!bg-primary text-white md:w-[35px] md:h-[35px] w-[25px] h-[25px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                    currentPage === totalPages ? "" : "cursor-pointer"
                  }`}
                >
                  <img src={RightAngle} className="invert grayscale brightness-100" alt="" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="rounded-lg px-4 py-6 bg-white mb-4 shadow-sm !mt-5 text-center text-gray-500">
            Could not load fuel operator details.
            <div className="mt-3">
              <Link to="/fuel-operator-management" className="btn btn-primary">
                Back to list
              </Link>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ViewFuelOperatorDetails;
